import React, { useState } from "react";
import { QueryObserverResult, useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../../../common/VFX-UI/";

//App
import LookupType from "./LookupType";
import lookupTypeApi from "../../../../app/model/api/lookupTypeApi";
import {
  LookupTypeContextData,
  LookupTypeState,
  LookupTypeApiResponse,
} from "../../../../types/lookupType";
import {
  selectIsActive,
  selectSearchValue,
  setIsActive,
  setSearchValue,
} from "../../../../store/lookuptype";
import { SearchDefaultValue, SearchValue } from "../../../../store/types";
import {
  useLookupTypesData,
  useLookupTypeQuery,
  useLookupTypeGridQuery,
} from "./hooks/useLookupType";
import { GRID_PAGE_SIZE } from "../../../../utility/constants";
import { SortBy } from "../../../../types";

export const LookupTypeContext =
  React.createContext<LookupTypeContextData | null>(null);

const LookupTypeContainer = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState<LookupTypeState>({
    lookupTypeId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? SearchDefaultValue,
    currentPage: 1,
    pageSize: GRID_PAGE_SIZE,
    sortBy: [{ field: "name", direction: 1 }],
  });

 

  const lookupTypeGridQuery = useLookupTypeGridQuery(state);

  const lookupTypeQuery = useLookupTypeQuery(state.lookupTypeId);

  const updateLookupTypeId = async (lookupTypeId: string | null) =>
    setState((prevState) => ({
      ...prevState,
      lookupTypeId: lookupTypeId,
    }));

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await lookupTypeApi.delete(value);
    },
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await lookupTypeApi.activate(value);
    },
  });

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) =>
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const lookupTypes = useLookupTypesData(lookupTypeGridQuery);

  const contextValue: LookupTypeContextData = {
    data: lookupTypeQuery?.data?.result,
    id: state.lookupTypeId,
    activeToggle: state.activeToggle,
    searchValue: state.searchPayload ?? undefined,
    paging: {
      currentPage: state.currentPage,
      totalSize: lookupTypeGridQuery?.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    setId: updateLookupTypeId,
    refetchGrid: () =>
      lookupTypeGridQuery.refetch() as Promise<
        QueryObserverResult<LookupTypeApiResponse>
      >,
    setActiveToggleValue: updateActiveToggle,
    setSearchValue: updateSearchPayload,
    setCurrentPage: updateCurrentPage,
    setCurrentPageSize: updateCurrentPageSize,
    setSortBy: updateSortBy,
  };

  return lookupTypeGridQuery.isLoading ? (
    <LoadingIndicator />
  ) : (
    <LookupTypeContext.Provider value={contextValue}>
      <LookupType
        data={lookupTypes}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
      />
    </LookupTypeContext.Provider>
  );
};

export default LookupTypeContainer;
